import React from 'react';
import { Link } from 'react-router-dom';

export function getInvoiceLevel(invoice) {
    const level = parseInt(invoice && invoice.invoice_level, 10);
    if (!Number.isNaN(level)) {
        return level;
    }

    if (!invoice || !invoice.parent_iid) {
        return 0;
    }

    const parentLevel = parseInt(invoice.parent_invoice && invoice.parent_invoice.invoice_level, 10);
    if (!Number.isNaN(parentLevel)) {
        return parentLevel + 1;
    }

    return 1;
}

export function getInvoiceOptionLabel(invoice) {
    if (!invoice) {
        return '';
    }

    if (Array.isArray(invoice.invoice_chain) && invoice.invoice_chain.length > 0) {
        return invoice.invoice_chain
            .filter((item) => item && item.name)
            .map((item) => item.name)
            .join(' / ');
    }

    if (invoice.invoice_chain_label) {
        return invoice.invoice_chain_label.replace(/\s*==>\s*/g, ' / ');
    }

    return invoice.name || '';
}

export function getInvoiceOption(invoice, options = {}) {
    if (!invoice) {
        return invoice;
    }

    const { currentOnly = false } = options;
    const fullLabel = getInvoiceOptionLabel(invoice);
    const label = currentOnly ? (invoice.name || fullLabel) : fullLabel;

    return {
        ...invoice,
        label: label,
        searchText: fullLabel || label,
    };
}

export function buildInvoiceChainItems(invoice) {
    if (!invoice) {
        return [];
    }

    if (Array.isArray(invoice.invoice_chain) && invoice.invoice_chain.length > 0) {
        return invoice.invoice_chain
            .filter((item) => item && item.name)
            .map((item) => ({
                id: item.id,
                name: item.name,
            }));
    }

    if (invoice.id && invoice.name) {
        return [{
            id: invoice.id,
            name: invoice.name,
        }];
    }

    return [];
}

export function buildProductInvoiceChainItems(product) {
    if (!product) {
        return [];
    }

    const items = [];
    if (parseInt(product.iid, 10) > 0 && product.invoice_name) {
        items.push({
            id: product.iid,
            name: product.invoice_name,
        });
    }

    if (parseInt(product.final_iid, 10) > 0 && product.final_invoice_name) {
        items.push({
            id: product.final_iid,
            name: product.final_invoice_name,
        });
    }

    if (parseInt(product.final_iid_2, 10) > 0 && product.final_invoice_name_2) {
        items.push({
            id: product.final_iid_2,
            name: product.final_invoice_name_2,
        });
    }

    return items;
}

export function buildSearchInvoiceChainItems(source) {
    return buildProductInvoiceChainItems(source);
}

export function InvoiceChain({ items, linkMode = 'a', target = '_blank', empty = null }) {
    const chainItems = (items || []).filter((item) => item && item.name);

    if (chainItems.length === 0) {
        return empty;
    }

    return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', lineHeight: 1.4 }}>
            {chainItems.map((item, index) => {
                const key = `${item.id || item.name}-${index}`;
                const content = linkMode === 'router'
                    ? <Link to={`/invoice/${item.id}`} target={target}>{item.name}</Link>
                    : linkMode === 'a'
                        ? <a href={`/invoice/${item.id}`} target={target} rel="noopener noreferrer">{item.name}</a>
                        : <span>{item.name}</span>;

                return (
                    <span key={key} style={{ display: 'inline-flex', alignItems: 'center', paddingLeft: `${index * 14}px` }}>
                        {index > 0 ? <span style={{ color: '#999', marginRight: '6px' }}>└</span> : null}
                        {item.id ? content : <span>{item.name}</span>}
                    </span>
                );
            })}
        </span>
    );
}
